/*
Copyright (C) 2020 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React from 'react';

import useRefreshEffect from '@broadleaf/admin-components/dist/common/hooks/useRefreshEffect';
import useTracking from '@broadleaf/admin-components/dist/tracking/hooks/useTracking';
import useTranslateMode from '@broadleaf/admin-components/dist/form/hooks/useTranslateMode';
import ItemBrowser from '../../../form/item-browser';
import useItemBrowser from '@broadleaf/admin-components/dist/form/item-browser/hooks/useItemBrowser';
import ActionRegistrar from '@broadleaf/admin-components/dist/metadata/services/ActionRegistrar';
import {
  getActions,
  getAttribute,
  getEndpointByType,
  hasEndpointWithType,
  isTranslatable
} from '@broadleaf/admin-components/dist/metadata/utils/MetadataUtils';
import { ContextParameters } from '@broadleaf/admin-components/dist/tracking/utils/ContextRequest';
import type { IMetadata } from '@broadleaf/admin-components/dist/types/metadata';

export const AssetBrowserView: React.FC<AssetBrowserViewProps> = props => {
  const tracking = useTracking();
  const translateMode = useTranslateMode();
  const locale = isTranslatable(props.metadata)
    ? translateMode.locale
    : undefined;
  const sharedContextParams = {
    [ContextParameters.LOCALE]: locale,
    [ContextParameters.TRACKING]: tracking
  };
  const idKey = getAttribute(props.metadata, 'idKey', 'id');
  const itemFolderIdKey = getAttribute(
    props.metadata,
    'itemFolderIdKey',
    'folderId'
  );
  const folderIdKey = getAttribute(props.metadata, 'folderIdKey', 'id');
  const folderParentIdKey = getAttribute(
    props.metadata,
    'folderParentIdKey',
    'parentFolderId'
  );
  const {
    getProviderProps,
    handleCreate,
    handleCreateFolder,
    handleDelete,
    handleDeleteFolder,
    handleRead,
    handleReadRootFolders,
    handleReadSubFolders,
    handleReinitialize,
    handleUpdate,
    handleUpdateFolder,
    handleUpload
  } = useItemBrowser({
    createConfig: getEndpointByType(props.metadata, 'CREATE'),
    createContextParams: sharedContextParams,
    createFolderConfig: getEndpointByType(props.metadata, 'CREATE_FOLDER'),
    createFolderContextParams: sharedContextParams,
    deleteConfig: getEndpointByType(props.metadata, 'DELETE'),
    deleteContextParams: sharedContextParams,
    deleteFolderConfig: getEndpointByType(props.metadata, 'DELETE_FOLDER'),
    deleteFolderContextParams: sharedContextParams,
    folderIdKey,
    folderParentIdKey,
    idKey,
    importConfig: getEndpointByType(props.metadata, 'IMPORT'),
    importContextParams: sharedContextParams,
    importReadStateConfig: getEndpointByType(
      props.metadata,
      'IMPORT_READ_STATE'
    ),
    importReadStateContextParams: sharedContextParams,
    postUploadConfig: getEndpointByType(props.metadata, 'POST_UPLOAD'),
    itemFolderIdKey,
    postUploadContextParams: sharedContextParams,
    // for our read configuration, we support a different read configuration when filtering by the currently selected folder
    readConfig: ({ folderId }) => {
      const readEndpoint = getEndpointByType(props.metadata, 'READ');
      if (folderId) {
        return (
          getEndpointByType(props.metadata, 'READ_BY_FOLDER') || readEndpoint
        );
      }
      return readEndpoint;
    },
    readContextParams: sharedContextParams,
    readRootFoldersConfig: getEndpointByType(
      props.metadata,
      'READ_ROOT_FOLDERS'
    ),
    readRootFoldersContextParams: sharedContextParams,
    readSubFoldersConfig: getEndpointByType(props.metadata, 'READ_SUB_FOLDERS'),
    readSubFoldersContextParams: sharedContextParams,
    updateConfig: getEndpointByType(props.metadata, 'UPDATE'),
    updateContextParams: sharedContextParams,
    updateFolderConfig: getEndpointByType(props.metadata, 'UPDATE_FOLDER'),
    updateFolderContextParams: sharedContextParams,
    uploadConfig: getEndpointByType(props.metadata, 'UPLOAD'),
    uploadContextParams: sharedContextParams
  });

  /**
   * This hook ensures that our results are refreshed when the tracking context changes.
   */
  useRefreshEffect(
    () => {
      handleReinitialize();
      handleRead();
      handleReadRootFolders();
    },
    { tracking },
    undefined,
    false
  );

  /**
   * This hook ensures that our item results are refreshed when the locale changes to fetch the translated fields.
   */
  useRefreshEffect(handleRead, { locale }, undefined, false);

  const itemBrowserProps = {
    actionComponents: ActionRegistrar.getActionComponents(
      props.metadata.classifier,
      props.metadata.type
    ),
    actionDefinitions: getActions(props.metadata),
    altKey: getAttribute(props.metadata, 'altKey', 'alt'),
    className: 'tw-h-full',
    embedCodeKey: getAttribute(props.metadata, 'embedCodeKey', 'embedCode'),
    enableDetails: true,
    enableDetailsUpdates: hasEndpointWithType(props.metadata, 'UPDATE'),
    enableFolders: hasEndpointWithType(props.metadata, 'READ_ROOT_FOLDERS'),
    enableUploads: hasEndpointWithType(props.metadata, 'UPLOAD'),
    folderIdKey,
    folderParentIdKey,
    handleCreate,
    handleCreateFolder,
    handleDelete,
    handleDeleteFolder,
    handleRead,
    handleReadRootFolders,
    handleReadSubFolders,
    handleUpdate,
    handleUpdateFolder,
    handleUpload,
    headerTitle: props.metadata.label,
    idKey,
    mimeTypeKey: getAttribute(props.metadata, 'mimeTypeKey', 'mimeType'),
    srcKey: getAttribute(props.metadata, 'srcKey', 'url'),
    tagsKey: getAttribute(props.metadata, 'tagsKey', 'tags'),
    titleKey: getAttribute(props.metadata, 'titleKey', 'title'),
    typeKey: getAttribute(props.metadata, 'typeKey', 'type')
  };
  return (
    <ItemBrowser.Provider {...getProviderProps()}>
      <ItemBrowser.Display {...itemBrowserProps} />
      <ItemBrowser.Modals
        modalProps={{
          handleCreate,
          handleCreateFolder,
          handleDelete,
          handleDeleteFolder,
          handleRead,
          handleReadRootFolders,
          handleReadSubFolders,
          handleUpdate,
          handleUpdateFolder,
          handleUpload,
          folderIdKey,
          folderParentIdKey
        }}
      />
    </ItemBrowser.Provider>
  );
};

export interface AssetBrowserViewProps {
  history: object;
  location: object;
  metadata: IMetadata;
}

export default AssetBrowserView;
